import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { callerProfiles } from '@/api/callerProfiles';
import type { CallerProfileRow, CreateCallerBody } from '@/api/callerProfiles';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useEffectiveCustomerId } from '@/lib/customer-context-store';
import { useCustomerPicker } from '@/components/CustomerPicker';
import { CustomerScopeChip } from '@/components/CustomerScopeChip';
import { formatRef } from '@/lib/formatRef';
import { PageHeader } from '@/components/PageHeader';
import { DataTable } from '@/components/DataTable';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Plus, MoreHorizontal, Trash2, PhoneOutgoing, Key, Phone } from 'lucide-react';
import { PhoneInput } from '@/components/PhoneInput';

const CAN_MUTATE = ['super_admin', 'reseller_owner', 'office_manager'];

export function CallerProfilesList() {
  const { t } = useTranslation();
  const user = useCurrentUser();
  const effectiveCustomerId = useEffectiveCustomerId();
  const qc = useQueryClient();
  const canMutate = CAN_MUTATE.includes(user.role);
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<CallerProfileRow | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CallerProfileRow | null>(null);
  const [phonesTarget, setPhonesTarget] = useState<CallerProfileRow | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['callerProfiles', search, effectiveCustomerId],
    queryFn: () => callerProfiles.list({
      search: search.trim() || undefined,
      limit: 200,
      customer_id: effectiveCustomerId ?? undefined,
    }),
  });
  const rows = data?.rows ?? [];

  const remove = useMutation({
    mutationFn: (id: string) => callerProfiles.remove(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['callerProfiles'] }); setDeleteTarget(null); },
  });

  const columns = [
    { header: t('callerProfiles.columnRef'), cell: (r: CallerProfileRow) => <span className="font-mono text-xs text-slate-500">{formatRef(r.ref_number, 'CAL')}</span> },
    { header: t('callerProfiles.columnName'), cell: (r: CallerProfileRow) => (
      r.display_name
        ? <span className="text-sm font-medium">{r.display_name}</span>
        : <span className="text-xs text-slate-400">—</span>
    ) },
    { header: t('callerProfiles.columnEmail'), cell: (r: CallerProfileRow) => (
      r.email ? <span className="text-xs text-slate-600 font-mono">{r.email}</span>
      : <span className="text-xs text-slate-300">—</span>
    ) },
    { header: t('callerProfiles.columnPin'), cell: (r: CallerProfileRow) => (
      r.has_pin ? <Badge variant="outline" className="text-[10px]"><Key className="h-2.5 w-2.5 mr-1" />{t('callerProfiles.pinSet')}</Badge>
      : <span className="text-xs text-slate-300">{t('callerProfiles.pinNone')}</span>
    ) },
    { header: t('callerProfiles.columnPoints'), cell: (r: CallerProfileRow) => (
      <span className="font-mono text-xs tabular-nums">{r.points_balance}</span>
    ) },
    { header: t('callerProfiles.columnLang'), cell: (r: CallerProfileRow) => (
      <span className="text-xs uppercase tracking-wider text-slate-500">{r.language}</span>
    ) },
    { header: t('callerProfiles.columnActive'), cell: (r: CallerProfileRow) => (
      r.active ? <Badge variant="default">{t('common.active')}</Badge> : <Badge variant="secondary">{t('common.inactive')}</Badge>
    ) },
    { header: '', className: 'w-10 text-end', cell: (r: CallerProfileRow) => (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={t('common.actions')}
            onClick={(e) => e.stopPropagation()}
            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors"
          >
            <MoreHorizontal className="h-4 w-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
          <DropdownMenuItem onClick={() => setPhonesTarget(r)}>
            <Phone className="h-3.5 w-3.5 mr-2" />
            {t('callerProfiles.phonesTitle')}
          </DropdownMenuItem>
          {canMutate && (
            <DropdownMenuItem onClick={() => setEditTarget(r)}>
              <Key className="h-3.5 w-3.5 mr-2" />
              {t('common.edit')}
            </DropdownMenuItem>
          )}
          {canMutate && (
            <DropdownMenuItem
              className="text-red-600 focus:text-red-600 focus:bg-red-50"
              onClick={() => setDeleteTarget(r)}
            >
              <Trash2 className="h-3.5 w-3.5 mr-2" />
              {t('common.delete')}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    ) },
  ];

  return (
    <>
      <PageHeader
        eyebrow={t('navGroups.accounts')}
        title={t('callerProfiles.title')}
        description={t('callerProfiles.description')}
        actions={
          <div className="flex items-center gap-2">
            <Input
              value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder={t('callerProfiles.searchPlaceholder')}
              className="h-9 w-56 text-sm"
            />
            {canMutate && (
              <Button size="sm" onClick={() => setCreateOpen(true)}>
                <Plus className="h-4 w-4 mr-1.5" />
                {t('callerProfiles.newProfile')}
              </Button>
            )}
          </div>
        }
      />

      {isLoading ? (
        <div className="text-sm text-slate-400">{t('common.loading')}</div>
      ) : (
        <DataTable
          rows={rows}
          columns={columns}
          empty={search
            ? t('callerProfiles.noMatch', { query: search })
            : t('callerProfiles.noProfilesYet')}
        />
      )}

      <CreateCallerDialog open={createOpen} onOpenChange={setCreateOpen} />
      {editTarget && <EditCallerDialog profile={editTarget} onClose={() => setEditTarget(null)} />}
      {deleteTarget && (
        <Dialog open onOpenChange={(v) => { if (!v) setDeleteTarget(null); }}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>{t('callerProfiles.deleteTitle')}</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-slate-600">
              {t('callerProfiles.deleteBody', {
                name: deleteTarget.display_name ?? t('callerProfiles.unnamedCaller'),
                ref: deleteTarget.ref_number,
              })}
            </p>
            <DialogFooter>
              <DialogClose asChild><Button type="button" variant="outline" disabled={remove.isPending}>{t('common.cancel')}</Button></DialogClose>
              <Button variant="destructive" disabled={remove.isPending} onClick={() => remove.mutate(deleteTarget.id)}>
                {remove.isPending ? t('common.deleting') : t('common.delete')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {phonesTarget && <PhonesDialog profile={phonesTarget} onClose={() => setPhonesTarget(null)} canMutate={canMutate} />}
    </>
  );
}

// ── Create dialog ─────────────────────────────────────────────────────────────

function CreateCallerDialog({
  open, onOpenChange,
}: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [pin, setPin] = useState('');
  const [phoneRaw, setPhoneRaw] = useState('');
  const [lang, setLang] = useState<'en' | 'he' | 'yi'>('en');
  const [error, setError] = useState<string | null>(null);
  const [customerId, setCustomerId] = useState<string | null>(null);
  const { effectiveCustomerId, element: customerPicker, isPickerVisible } =
    useCustomerPicker(customerId, setCustomerId, { required: true });

  const create = useMutation({
    mutationFn: (body: CreateCallerBody) => callerProfiles.create(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['callerProfiles'] });
      setDisplayName(''); setEmail(''); setPin(''); setPhoneRaw(''); setLang('en');
      setCustomerId(null); setError(null);
      onOpenChange(false);
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : t('common.failedToSave'));
    },
  });

  function submit() {
    setError(null);
    if (effectiveCustomerId === null) { setError(t('callerProfiles.customerRequired')); return; }
    // Split on commas / semicolons / newlines ONLY — spaces are legal inside
    // a single national number now that the backend prefixes national input
    // with the account's default country code ("555 123 4567" is one number).
    const phones = phoneRaw.split(/[,;\n]+/).map((p) => p.trim()).filter(Boolean);
    const body: CreateCallerBody = {
      display_name: displayName.trim() || null,
      email: email.trim() || null,
      pin: pin.trim() || null,
      language: lang,
      phone_numbers: phones.length > 0 ? phones : undefined,
      ...(effectiveCustomerId
        ? { customer_id: effectiveCustomerId }
        : {}),
    };
    create.mutate(body);
  }

  const canSubmit = effectiveCustomerId !== null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('callerProfiles.newTitle')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {customerPicker}
          <CustomerScopeChip />
          <div className="space-y-1">
            <Label htmlFor="cp-name">{t('callerProfiles.displayNameLabel')}</Label>
            <Input id="cp-name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder={t('callerProfiles.displayNamePlaceholder')} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="cp-email">{t('callerProfiles.emailOptional')}</Label>
            <Input id="cp-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="cp-pin">{t('callerProfiles.pinLabel')}</Label>
            <Input id="cp-pin" type="password" value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 10))} autoComplete="off" />
            <p className="text-xs text-muted-foreground">{t('callerProfiles.pinHint')}</p>
          </div>
          <div className="space-y-1">
            <Label htmlFor="cp-phones">{t('callerProfiles.phonesLabel')}</Label>
            <Input id="cp-phones" value={phoneRaw} onChange={(e) => setPhoneRaw(e.target.value)} placeholder={t('callerProfiles.phonesPlaceholder')} />
            <p className="text-xs text-muted-foreground">{t('callerProfiles.phonesHint')}</p>
          </div>
          <div className="space-y-1">
            <Label>{t('callerProfiles.languageLabel')}</Label>
            <Select value={lang} onValueChange={(v) => setLang(v as typeof lang)}>
              <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="en">English</SelectItem>
                <SelectItem value="he">Hebrew</SelectItem>
                <SelectItem value="yi">Yiddish</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <DialogClose asChild><Button type="button" variant="outline" disabled={create.isPending}>{t('common.cancel')}</Button></DialogClose>
          <Button type="button" onClick={submit} disabled={create.isPending || !canSubmit}>
            {create.isPending ? t('callerProfiles.creating') : t('callerProfiles.createProfile')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Edit dialog ───────────────────────────────────────────────────────────────

function EditCallerDialog({ profile, onClose }: { profile: CallerProfileRow; onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [displayName, setDisplayName] = useState(profile.display_name ?? '');
  const [email, setEmail] = useState(profile.email ?? '');
  const [newPin, setNewPin] = useState('');
  const [clearPin, setClearPin] = useState(false);
  const [active, setActive] = useState(profile.active);
  const [error, setError] = useState<string | null>(null);

  const patch = useMutation({
    mutationFn: () => callerProfiles.update(profile.id, {
      display_name: displayName.trim() || null,
      email: email.trim() || null,
      pin: newPin ? newPin : undefined,
      clear_pin: clearPin || undefined,
      active,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['callerProfiles'] });
      onClose();
    },
    onError: (err) => setError(err instanceof Error ? err.message : t('common.failedToSave')),
  });

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('callerProfiles.editTitle', { name: profile.display_name ?? formatRef(profile.ref_number, 'CAL') })}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1">
            <Label htmlFor="ec-name">{t('callerProfiles.displayNameLabel')}</Label>
            <Input id="ec-name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="ec-email">{t('common.email')}</Label>
            <Input id="ec-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div className="space-y-2 pt-2 border-t border-border">
            <Label>{t('callerProfiles.columnPin')}</Label>
            {profile.has_pin && !clearPin && (
              <button
                type="button"
                className="text-xs text-destructive underline underline-offset-2 hover:no-underline block"
                onClick={() => setClearPin(true)}
              >
                {t('callerProfiles.clearPin')}
              </button>
            )}
            {clearPin && (
              <p className="text-xs text-amber-700">
                {t('callerProfiles.pinWillBeCleared')}{' '}
                <button type="button" className="underline" onClick={() => setClearPin(false)}>{t('callerProfiles.undo')}</button>
              </p>
            )}
            {!clearPin && (
              <Input
                type="password" autoComplete="off"
                value={newPin}
                onChange={(e) => setNewPin(e.target.value.replace(/\D/g, '').slice(0, 10))}
                placeholder={profile.has_pin ? t('callerProfiles.pinRotatePlaceholder') : t('callerProfiles.pinSetPlaceholder')}
              />
            )}
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} className="accent-primary" />
            {t('callerProfiles.activeLabel')}
          </label>
          {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <DialogClose asChild><Button type="button" variant="outline" disabled={patch.isPending}>{t('common.cancel')}</Button></DialogClose>
          <Button type="button" onClick={() => patch.mutate()} disabled={patch.isPending}>
            {patch.isPending ? t('common.saving') : t('common.saveChanges')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Phones dialog ─────────────────────────────────────────────────────────────

function PhonesDialog({
  profile, onClose, canMutate,
}: { profile: CallerProfileRow; onClose: () => void; canMutate: boolean }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [newE164, setNewE164] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { data } = useQuery({
    queryKey: ['callerPhones', profile.id],
    queryFn: () => callerProfiles.listPhones(profile.id),
  });
  const phones = data?.rows ?? [];

  const add = useMutation({
    mutationFn: () => callerProfiles.addPhone(profile.id, { e164: newE164.trim() }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['callerPhones', profile.id] });
      setNewE164(''); setError(null);
    },
    onError: (err) => setError(err instanceof Error ? err.message : t('common.failedToSave')),
  });
  const remove = useMutation({
    mutationFn: (phoneId: string) => callerProfiles.removePhone(profile.id, phoneId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['callerPhones', profile.id] }),
  });

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PhoneOutgoing className="h-4 w-4 text-muted-foreground" />
            {t('callerProfiles.phonesTitle')} · {profile.display_name ?? formatRef(profile.ref_number, 'CAL')}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <ul className="divide-y divide-border rounded-md border border-border overflow-hidden">
            {phones.length === 0 && (
              <li className="px-4 py-6 text-sm text-muted-foreground text-center">{t('callerProfiles.noPhoneNumbers')}</li>
            )}
            {phones.map((p) => (
              <li key={p.id} className="flex items-center justify-between gap-3 px-4 py-2.5">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="font-mono text-sm">{p.e164}</span>
                  {p.is_primary && <Badge variant="outline" className="text-[10px]">{t('callerProfiles.primary')}</Badge>}
                </div>
                {canMutate && (
                  <Button size="sm" variant="ghost" onClick={() => remove.mutate(p.id)} disabled={remove.isPending}>
                    <Trash2 className="h-3.5 w-3.5 text-slate-400" />
                  </Button>
                )}
              </li>
            ))}
          </ul>
          {canMutate && (
            <div className="flex gap-2 pt-3 border-t border-border">
              <PhoneInput
                value={newE164}
                onChange={setNewE164}
                customerId={profile.customer_id}
                placeholder="800 555 1234"
                inputClassName="text-sm h-9"
                className="flex-1"
              />
              <Button
                size="sm"
                onClick={() => { if (newE164.trim()) add.mutate(); }}
                disabled={add.isPending || !/^\+[1-9]\d{1,14}$/.test(newE164.trim())}
              >
                {t('callerProfiles.add')}
              </Button>
            </div>
          )}
          {error && <p role="alert" className="text-xs text-destructive">{error}</p>}
          <p className="text-xs text-muted-foreground">
            {t('callerProfiles.phonesE164Hint')}
          </p>
        </div>
        <DialogFooter>
          <DialogClose asChild><Button type="button" variant="outline">{t('common.close')}</Button></DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
